import mixpanel, { Dict } from 'mixpanel-browser';

import { keys } from '../../../keys';
import type { AnalyticsProvider } from '../types';

export type EventProperties = Dict | undefined;

class MixpanelAnalyticsProvider implements AnalyticsProvider {
  private isInitialized = false;

  public initialize = async (): Promise<void> => {
    if (typeof window === 'undefined' || this.isInitialized) return;

    const token = keys().NEXT_PUBLIC_ANALYTICS_MIXPANEL_TOKEN;

    if (!token) {
      console.warn('[Mixpanel] Token not found.');
      return;
    }

    mixpanel.init(token, {
      debug: process.env.NODE_ENV !== 'production',
      track_pageview: false
    });

    this.isInitialized = true;
  };

  public trackPageView = async (path: string): Promise<void> => {
    if (typeof window === 'undefined') return;

    if (!this.isInitialized) {
      await this.initialize();
    }

    if (this.isInitialized) {
      mixpanel.track('Page View', {
        page: path
      });
    }
  };

  public trackEvent = async (
    eventName: string,
    eventProperties: Record<string, string | string[] | number | boolean> = {}
  ): Promise<void> => {
    if (typeof window === 'undefined') return;

    if (!this.isInitialized) {
      await this.initialize();
    }

    if (this.isInitialized) {
      const processedProps: Record<string, string | number | boolean> = {};
      Object.entries(eventProperties).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          processedProps[key] = value.join(',');
        } else if (value !== null && value !== undefined) {
          processedProps[key] = value;
        }
      });

      mixpanel.track(eventName, processedProps);
    }
  };

  public identify = async (
    userId: string,
    traits: Record<string, string | number | boolean> = {}
  ): Promise<void> => {
    if (typeof window === 'undefined') return;

    if (!this.isInitialized) {
      await this.initialize();
    }

    if (this.isInitialized) {
      mixpanel.identify(userId);
      if (Object.keys(traits).length > 0) {
        mixpanel.people.set(traits);
      }
    }
  };
}

export default new MixpanelAnalyticsProvider();
